import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { AccountInfo } from '@azure/msal-browser';
import { CalAngularService } from '@cvx/cal-angular';
import { RefineryApiService, RoleApiService, WeldManagerUserApiService } from '../api';
import { environment } from '@env/environment';
import { UserPermissionService } from 'src/app/data/services/user-permission.service';

export interface AuthenticatedUser {
  userId: string;
  userName: string;
  refineryId: number;
  refineryName: string;
  roleId: number;
  roleName: string;
  isAdminAtAllSites: boolean;
  permissions: string[];
}

@Injectable()
export class AuthService {
  //#region 'Variables'
  private userInfo: AccountInfo;
  private readonly verifiedUserSubject = new BehaviorSubject<AuthenticatedUser | null>(null);
  public readonly verifiedUser$ = this.verifiedUserSubject.asObservable();

  public get verifiedUser(): AuthenticatedUser | null {
    return this.verifiedUserSubject.value;
  }
  static calConfig = {
    autoSignIn: true,
    popupForLogin: false,
    cacheLocation: 'localStorage',
    instance: 'https://login.microsoftonline.com/',
    tenantId: 'fd799da1-bfc1-4234-a91c-72b3a1cb9e26',
    clientId: AuthService.getClientId(),
    redirectUri: AuthService.getRedirectUri(),
    oidcScopes: ['openid', 'profile', 'User.Read', 'offline_access'],
    graphScopes: ['User.Read']
  };
  //#endregion

  //#region 'Angular Life Cycle'
  constructor(
    private readonly _srvCal: CalAngularService,
    private readonly router: Router,
    private readonly _srvWeldUser: WeldManagerUserApiService,
    private readonly _srvRole: RoleApiService,
    private readonly authService: CalAngularService,
    private readonly _srvRefinery: RefineryApiService,
    private readonly _srvPermissions: UserPermissionService
  ) {}
  //#endregion

  //#region 'Login'
  public login() {
    this._srvCal
      .isUserSignedIn()
      .toPromise()
      .then((isLoggedIn: boolean) => {
        if (isLoggedIn) {
          this.getUserInfo();
        } else {
          setTimeout(() => {
            this.login();
          }, 1000);
        }
      });
  }

  public getCurrentUser() {
    return this.authService.getAccount();
  }

  public getUserInfo() {
    this.userInfo = this._srvCal.getAccount();
    if (this.userInfo == null) {
      console.error('User info not found not found');
    } else {
      this.loadRefineryId(this.userInfo.localAccountId);
    }
  }
  //#endregion

  //#region 'Gets'
  private static getClientId(): string {
    const hostname = window?.location?.hostname;
    const testClientId = '3e19cf48-cced-4e7b-beee-6ecd98de9472';
    const prodClientId = '37573605-58b5-4ae5-a95a-925d78c165e6';

    if (
      hostname.includes(environment.host.localhost) ||
      hostname.includes(environment.host.dev) ||
      hostname.includes(environment.host.test)
    ) {
      return testClientId;
    } else {
      return prodClientId;
    }
  }

  private static getRedirectUri(): string {
    const hostname = window?.location?.hostname;

    if (hostname.includes(environment.host.localhost)) {
      return 'http://localhost:4200';
    } else if (hostname.includes(environment.host.dev)) {
      return 'https://wldmgr-dev.azure.chevron.com';
    } else if (hostname.includes(environment.host.test)) {
      return 'https://wldmgr-test.azure.chevron.com';
    } else {
      return 'https://wldmgr.azure.chevron.com';
    }
  }
  //#endregion

  //#region 'General Methods'
  public getScopes(): string[] {
    return [`${AuthService.getClientId()}/user_impersonation`];
  }

  private loadRefineryId(userId: string): void {
    if (userId) {
      this._srvWeldUser.GetUserRolesByUserAsync(userId).subscribe({
        next: (res: any) => {
          if (res.length > 0) {
            let selectedSite: { userId?: string; refineryId: number } = null;
            try {
              selectedSite = JSON.parse(sessionStorage.getItem('selectedSite'));
            } catch {
              sessionStorage.removeItem('selectedSite');
            }
            if (selectedSite?.userId) {
              if (selectedSite.userId !== userId) {
                sessionStorage.removeItem('selectedSite');
                selectedSite = null;
              } else {
                sessionStorage.setItem('selectedSite', JSON.stringify({ refineryId: Number(selectedSite.refineryId) }));
              }
            }
            if (selectedSite && Number.isInteger(Number(selectedSite.refineryId))) {
              this._srvWeldUser
                .GetUser(userId)
                .pipe(
                  switchMap(userResponse => {
                    const persistedUser = Array.isArray(userResponse) ? userResponse[0] : userResponse;
                    return forkJoin({
                      availableRoles: this._srvWeldUser.GetAvailableUserRolesByUserAsync(
                        userId,
                        Number(persistedUser.refineryId)
                      ),
                      assignments: this._srvWeldUser.GetAllUserRolesByUserAsync(userId),
                      roleCatalog: this._srvRole.GetRolesAsync()
                    });
                  })
                )
                .subscribe({
                  next: ({ availableRoles, assignments, roleCatalog }) => {
                    const selectedRole = availableRoles.find(
                      role =>
                        Number(role.refineryId) === Number(selectedSite.refineryId) &&
                        roleCatalog.some(catalogRole => Number(catalogRole.roleId) === Number(role.roleId)) &&
                        assignments.some(
                          assignment =>
                            Number(assignment.roleId) === Number(role.roleId) &&
                            (Number(assignment.active) === 1 || String(assignment.active).toLowerCase() === 'true')
                        )
                    );
                    if (!selectedRole) {
                      sessionStorage.removeItem('selectedSite');
                    }
                    this.loadUserRole(
                      this.resolveRoleName(selectedRole ? { ...res[0], ...selectedRole, userId } : res[0], roleCatalog)
                    );
                  },
                  error: (err: any) => {
                    if (err.status === 401) {
                      this.router.navigateByUrl('/error/not-authorized');
                    }
                  }
                });
            } else {
              this._srvRole.GetRolesAsync().subscribe({
                next: roleCatalog => this.loadUserRole(this.resolveRoleName(res[0], roleCatalog)),
                error: (err: any) => {
                  if (err.status === 401) {
                    this.router.navigateByUrl('/error/not-authorized');
                  }
                }
              });
            }
          } else {
            this.login();
          }
        },
        error: (err: any) => {
          if (err.status === 401) {
            this.router.navigateByUrl('/error/not-authorized');
          }
        }
      });
    }
  }

  private resolveRoleName(role: any, roleCatalog: any[]): any {
    const catalogRole = roleCatalog.find(item => Number(item.roleId) === Number(role?.roleId));
    return { ...role, roleName: catalogRole?.roleName };
  }

  private loadUserRole(selectedRole: any): void {
    const user: AuthenticatedUser = {
      refineryId: selectedRole.refineryId,
      refineryName: selectedRole.refineryName,
      roleId: selectedRole.roleId,
      roleName: selectedRole.roleName,
      userId: selectedRole.userId,
      userName: `${selectedRole.lastName}, ${selectedRole.firstName}`,
      isAdminAtAllSites: false,
      permissions: []
    };

    forkJoin({
      role: this._srvRole.GetRolesPermissionsByRefineryAsync(Number(user.refineryId)),
      isAdminAtAllSites: this.loadSuperAdminAccess(user.userId)
    }).subscribe({
      next: ({ role, isAdminAtAllSites }) => {
        const PERMISSIONS = role.filter(obj => Number(obj.roleId) === Number(user.roleId));
        user.permissions = PERMISSIONS.map(permission => permission.permissionName.toLowerCase());
        user.isAdminAtAllSites = isAdminAtAllSites;
        localStorage.removeItem('user');
        localStorage.setItem('user', JSON.stringify(user));
        this.verifiedUserSubject.next(user);
        this.router.navigateByUrl('/');
      },
      error: (err: any) => {
        if (err.status === 401) {
          this.router.navigateByUrl('/error/not-authorized');
        }
      }
    });
  }

  private loadSuperAdminAccess(userId: string): Observable<boolean> {
    return forkJoin({
      sites: this._srvRefinery.GetRefineriesAsync(),
      user: this._srvWeldUser.GetUser(userId),
      assignments: this._srvWeldUser.GetAllUserRolesByUserAsync(userId),
      roleCatalog: this._srvRole.GetRolesAsync()
    }).pipe(
      switchMap(({ sites, user, assignments, roleCatalog }) => {
        const persistedUser = Array.isArray(user) ? user[0] : user;
        const refineryId = Number(persistedUser?.refineryId);
        if (!Number.isInteger(refineryId) || refineryId <= 0) {
          return of(false);
        }

        return this._srvWeldUser.GetAvailableUserRolesByUserAsync(userId, refineryId).pipe(
          map(roles => {
            const resolvedRoles = roles.map(role => this.resolveRoleName(role, roleCatalog));
            return this._srvPermissions.isAdminAtAllSites(sites, resolvedRoles, assignments);
          })
        );
      }),
      catchError(error => {
        console.error('Unable to verify Super Admin site assignments.', error);
        return of(false);
      })
    );
  }

  public refreshSuperAdminAccess(): Observable<boolean> {
    const user = this._srvPermissions.currentUser;
    if (!user?.userId) {
      return of(false);
    }

    return this.loadSuperAdminAccess(user.userId).pipe(
      map(isAdminAtAllSites => {
        const currentUser = this._srvPermissions.currentUser;
        if (currentUser?.userId !== user.userId) {
          return false;
        }
        currentUser.isAdminAtAllSites = isAdminAtAllSites;
        localStorage.setItem('user', JSON.stringify(currentUser));
        this.verifiedUserSubject.next(currentUser);
        return this._srvPermissions.isSuperAdmin(currentUser);
      })
    );
  }

  //#endregion
}
